import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import PersonalizeCampaignsImage from './PersonalizeCampaignsImage'

const copy = {
  heading: `Personalize campaigns and optimize ad spend`,
}

const list = [
  {
    title: `Analyze`,
    description: `Optimize the shopping experience and drive purchases by analyzing user behavior and finding areas for experimentation.`,
  },
  {
    title: `Deliver`,
    description: `Deliver loyalty-reinforcing promotions wherever new and existing customers are shopping.`,
  },
  {
    title: `Optimize`,
    description: `Build audiences and create real-time campaigns to optimize ad spend.`,
  },
]

const link = {
  text: `Explore Twilio Engage`,
  url: `/product/twilio-engage/`,
}

const PersonalizeCampaigns: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper>
      <SectionBase variant="2Col" reverse={true}>
        <SectionCopy>
          <CopyTextBase>
            <StyledCopyTextHeading tag="h2">{copy.heading}</StyledCopyTextHeading>
            <ColumnTrack>
              {list.map(({ title, description }, index) => (
                <FeatureBoxBase key={index} alignment="left">
                  <FeatureBox.Body>
                    <FeatureBox.Title>{title}</FeatureBox.Title>
                    <FeatureBox.Text>{description}</FeatureBox.Text>
                  </FeatureBox.Body>
                </FeatureBoxBase>
              ))}
            </ColumnTrack>
            <CopyText.Link href={getAbsoluteUrl(link.url, pageConfig)}>{link.text}</CopyText.Link>
          </CopyTextBase>
        </SectionCopy>

        <StyledSectionContents>
          <PersonalizeCampaignsImage />
        </StyledSectionContents>
      </SectionBase>
    </Wrapper>
  )
}

const StyledSectionContents = styled(Section.Contents)`
  align-self: center;
`

const StyledCopyTextHeading = styled(CopyText.Heading)`
  margin-bottom: 40px;
`

const Wrapper = styled.div`
  display: grid;
`

const SectionCopy = styled(Section.Copy)`
  display: flex;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 392px;
    justify-self: end;
    align-self: center;
  }
`

export default PersonalizeCampaigns
